<template>
  <div v-if="contactId">
    <div v-if="!isDashboardAvailable">
      <p class="text-center text-muted">No permission available for contact dashboard</p>
    </div>
    <div v-if="isDashboardAvailable">
      <div class="row no-gutter" v-if="isCreditTypeAccount">
        <div class="col-12" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceAccount)">
          <CustomerAgedBalanceChartWidget :contactId="contactId">
          </CustomerAgedBalanceChartWidget>
        </div>
      </div>
      <div class="row no-gutter" v-if="isCreditTypeAccount">
        <div class="col-12" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceAccount)">
          <AccountsReceivableSummaryWidget :contactId="contactId" :gridHeight="320" />
        </div>
      </div>
      <div class="row no-gutter">
        <div class="col-12" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder) || userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceAccount)">
          <FinancialBreakdownWidget :contactId="contactId" :gridHeight="350" />
        </div>
      </div>
      <div class="row no-gutter" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder)">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="600" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-order-daily">
                <i class="tabbedbox-tab-icon fas fa-calendar-day"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Daily</div>
                  <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
                </div>
              </Tab>
              <Tab tabid="tab-order-monthly">
                <i class="tabbedbox-tab-icon fas fa-calendar-alt"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Monthly</div>
                  <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-order-daily" class="tabpage-padding-0">
                <OrderDailyChartWidget :gridHeight="540" :embedded="true" :contactId="contactId" />
              </TabPage>
              <TabPage tabid="tab-order-monthly" class="tabpage-padding-0">
                <OrderMonthlyChartWidget :gridHeight="540" :embedded="true" :contactId="contactId" />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>

      <div class="row no-gutter" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder)">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="620" large>
            <template v-slot:commoncontent>
              <div class="dashboard-filter-container analytics-border">
                <div class="row no-gutters">
                  <div class="col-6">
                    <ReportFilterPopover
                      headerTitle="Order Value Filter"
                      :showApplyButton="true"
                      :showResetButton="true"
                      :appendToBody="true"
                      alignment="left"
                      placement="bottom-end"
                      @apply="applyOrderValueFilter"
                      @reset="resetOrderValueFilter">
                      <template v-slot:popover>
                        <ReportDateRangeSelect 
                          :startDate="editingOrderValueFilter.startDate"
                          :endDate="editingOrderValueFilter.endDate"
                          @updateStartDate="editingOrderValueFilter.startDate = $event"
                          @updateEndDate="editingOrderValueFilter.endDate = $event"
                        />
                      </template>
                      <template v-slot:filterDisplay>
                        <div>
                          <i class="far fa-calendar mr-2"></i>{{appliedOrderValueFilter.startDate | formatDate}} - {{appliedOrderValueFilter.endDate | formatDate}}
                        </div>                
                      </template>
                    </ReportFilterPopover>
                  </div>
                  <div class="col-6 text-right">
                    <AnalyticsReportLink :href="orderReportLink">View Full Report</AnalyticsReportLink>
                  </div>
                </div>
              </div>
            </template>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-order-glass" @show="setOrderFullReportTab('tab-ordervalue-glass')">
                <i class="tabbedbox-tab-icon fas fa-layer-group"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Order Glass</div>
                  <div class="tabbedbox-tab-subtitle">Order Breakdown By Glass</div>
                </div>
              </Tab>
              <Tab tabid="tab-order-pricegroup" @show="setOrderFullReportTab('tab-ordervalue-pricegroup')">
                <i class="tabbedbox-tab-icon fas fa-money-check-edit-alt"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Order Price Group</div>
                  <div class="tabbedbox-tab-subtitle">Order Breakdown By Price Group</div>
                </div>
              </Tab>
              <Tab tabid="tab-order-glass-squaremeterage" @show="setOrderFullReportTab('tab-ordervalue-squaremeterage')">
                <i class="tabbedbox-tab-icon fas fa-ruler"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Order Square Meterage</div>
                  <div class="tabbedbox-tab-subtitle">Order Breakdown By Glass Square Meterage</div>
                </div>
              </Tab>
              <Tab tabid="tab-order-glass-services" @show="setOrderFullReportTab('tab-ordervalue-glassService')">
                <i class="tabbedbox-tab-icon fas fa-tools"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Order Glass Services</div>
                  <div class="tabbedbox-tab-subtitle">Order Breakdown By Glass Services</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-order-glass" class="tabpage-padding-2">
                <OrderValueChartWidget
                  :dateRangeType="ChartDateRangeTypes.custom"
                  type="orderValueGlass"
                  :gridHeight="500"
                  :contactId="contactId"
                  :embedded="true"
                  :showList="true"
                  :startDate="appliedOrderValueFilter.startDate"
                  :endDate="appliedOrderValueFilter.endDate"
                />
              </TabPage>
              <TabPage tabid="tab-order-pricegroup" class="tabpage-padding-2">
                <OrderValueChartWidget
                  :dateRangeType="ChartDateRangeTypes.custom"
                  type="orderValuePriceGroup"
                  :gridHeight="500"
                  :contactId="contactId"
                  :embedded="true"
                  :showList="true"
                  :startDate="appliedOrderValueFilter.startDate"
                  :endDate="appliedOrderValueFilter.endDate"
                />
              </TabPage>
              <TabPage tabid="tab-order-glass-squaremeterage" class="tabpage-padding-2">
                <OrderValueChartWidget
                  :dateRangeType="ChartDateRangeTypes.custom"
                  type="orderUnitsGlass"
                  :gridHeight="500"
                  :contactId="contactId"
                  :embedded="true"
                  :showList="true"
                  :startDate="appliedOrderValueFilter.startDate"
                  :endDate="appliedOrderValueFilter.endDate"
                />
              </TabPage>
              <TabPage tabid="tab-order-glass-services" class="tabpage-padding-2">
                <OrderValueChartWidget
                  :dateRangeType="ChartDateRangeTypes.custom"
                  type="orderValueGlassService"
                  :gridHeight="500"
                  :contactId="contactId"
                  :embedded="true"
                  :showList="true"
                  :startDate="appliedOrderValueFilter.startDate"
                  :endDate="appliedOrderValueFilter.endDate"
                />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>

      <div class="row no-gutter" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder)">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="600" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-ncr-itemvalue">
                <i class="tabbedbox-tab-icon fas fa-money-check-edit-alt"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">NCR Value</div>
                  <div class="tabbedbox-tab-subtitle">NCR Item Breakdown</div>
                </div>
              </Tab>
              <Tab tabid="tab-ncr-itemcount">
                <i class="tabbedbox-tab-icon fas fa-clipboard-list-check"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">NCR Count</div>
                  <div class="tabbedbox-tab-subtitle">NCR Item Breakdown</div>
                </div>
              </Tab>
              <Tab tabid="tab-ncr-orderbreakdown">
                <i class="tabbedbox-tab-icon fas fa-clipboard-list-check"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">NCR / Order</div>
                  <div class="tabbedbox-tab-subtitle">NCR / Order Breakdown</div>
                </div>
              </Tab>
              <Tab tabid="tab-ncr-orderitembreakdown">
                <i class="tabbedbox-tab-icon fas fa-clipboard-list-check"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">NCR / Order Item</div>
                  <div class="tabbedbox-tab-subtitle">NCR / Order Item Breakdown</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-ncr-itemvalue" class="tabpage-padding-2">
                <NCROrderChartWidget
                  :type="NCROrderChartTypes.ncrItemValue"
                  :gridHeight="450"
                  :embedded="true"
                  :showList="true"
                  :dateRangeType="ChartDateRangeTypes.custom"
                  :contactId="contactId"
                  :showDateFilter="true"
                  :startDate="appliedNCRFilter.startDate"
                  :endDate="appliedNCRFilter.endDate"
                  @updateEditingStartDate="editingNCRFilter.startDate = $event"
                  @updateEditingEndDate="editingNCRFilter.endDate = $event"
                  @dateApplied="applyNCRFilter"
                  @dateReset="resetNCRFilter"
                />
              </TabPage>
              <TabPage tabid="tab-ncr-itemcount" class="tabpage-padding-2">
                <NCROrderChartWidget
                  :type="NCROrderChartTypes.ncrItemCount"
                  :gridHeight="450"
                  :embedded="true"
                  :showList="true"
                  :dateRangeType="ChartDateRangeTypes.custom"
                  :contactId="contactId"
                  :showDateFilter="true"
                  :startDate="appliedNCRFilter.startDate"
                  :endDate="appliedNCRFilter.endDate"
                  @updateEditingStartDate="editingNCRFilter.startDate = $event"
                  @updateEditingEndDate="editingNCRFilter.endDate = $event"
                  @dateApplied="applyNCRFilter"
                  @dateReset="resetNCRFilter"
                />
              </TabPage>
              <TabPage tabid="tab-ncr-orderbreakdown" class="tabpage-padding-2">
                <NCROrderChartWidget
                  :type="NCROrderChartTypes.ncrOrderBreakdown"
                  :gridHeight="450"
                  :embedded="true"
                  :showList="true"
                  :dateRangeType="ChartDateRangeTypes.custom"
                  :contactId="contactId"
                  :showDateFilter="true"
                  :startDate="appliedNCRFilter.startDate"
                  :endDate="appliedNCRFilter.endDate"
                  @updateEditingStartDate="editingNCRFilter.startDate = $event"
                  @updateEditingEndDate="editingNCRFilter.endDate = $event"
                  @dateApplied="applyNCRFilter"
                  @dateReset="resetNCRFilter"
                />
              </TabPage>
              <TabPage tabid="tab-ncr-orderitembreakdown" class="tabpage-padding-2">
                <NCROrderChartWidget
                  :type="NCROrderChartTypes.ncrOrderItemBreakdown"
                  :gridHeight="450"
                  :embedded="true"
                  :showList="true"
                  :dateRangeType="ChartDateRangeTypes.custom"
                  :contactId="contactId"
                  :showDateFilter="true"
                  :startDate="appliedNCRFilter.startDate"
                  :endDate="appliedNCRFilter.endDate"
                  @updateEditingStartDate="editingNCRFilter.startDate = $event"
                  @updateEditingEndDate="editingNCRFilter.endDate = $event"
                  @dateApplied="applyNCRFilter"
                  @dateReset="resetNCRFilter"
                />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>

      <div class="row no-gutter" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceAccount)">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="600" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-invoice-daily">
                <i class="tabbedbox-tab-icon fas fa-calendar-day"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Daily</div>
                  <div class="tabbedbox-tab-subtitle">Invoice Value Chart</div>
                </div>
              </Tab>
              <Tab tabid="tab-invoice-monthly">
                <i class="tabbedbox-tab-icon fas fa-calendar-alt"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Monthly</div>
                  <div class="tabbedbox-tab-subtitle">Invoice Value Chart</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-invoice-daily" class="tabpage-padding-0">
                <InvoiceDailyChartWidget :gridHeight="540" :embedded="true" :contactId="contactId" />
              </TabPage>
              <TabPage tabid="tab-invoice-monthly" class="tabpage-padding-0">
                <InvoiceMonthlyChartWidget :gridHeight="540" :embedded="true" :contactId="contactId" />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>

      <div class="row no-gutter" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceDIFOT)">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="630" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-difot-monthly-operational">
                <i class="tabbedbox-tab-icon fas fa-truck"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Operational</div>
                  <div class="tabbedbox-tab-subtitle">Monthly Operational DIFOT</div>
                </div>
              </Tab>
              <Tab tabid="tab-difot-monthly-collection">
                <i class="tabbedbox-tab-icon fas fa-dolly-flatbed"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Collection</div>
                  <div class="tabbedbox-tab-subtitle">Monthly Collection Delay</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-difot-monthly-operational" class="tabpage-padding-2">
                <OrderDIFOTMonthlyOperationalWidget
                  :gridHeight="500"
                  :embedded="true"
                  :contactId="contactId"
                />
              </TabPage>
              <TabPage tabid="tab-difot-monthly-collection" class="tabpage-padding-2">
                <OrderDIFOTMonthlyCollectionWidget
                  :gridHeight="500"
                  :embedded="true"
                  :contactId="contactId"
                />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import moment from 'moment'
import buildUrl from 'build-url'

import DashboardContentBox from '@/components/dashboard/dashboardContentBox'
import DashboardTabbedContentBox from '@/components/dashboard/dashboardTabbedContentBox'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import CustomerAgedBalanceChartWidget from '@/modules/intelligence/dashboardWidgets/customerAgedBalanceChartWidget'
import AccountsReceivableSummaryWidget from '@/modules/intelligence/dashboardWidgets/accountsReceivableSummaryWidget'
import FinancialBreakdownWidget from '@/modules/intelligence/dashboardWidgets/financialBreakdownWidget'
import OrderValueChartWidget from '@/modules/intelligence/dashboardWidgets/orderValueChartWidget'
import OrderDailyChartWidget from '@/modules/intelligence/dashboardWidgets/orderDailyChartWidget'
import OrderMonthlyChartWidget from '@/modules/intelligence/dashboardWidgets/orderMonthlyChartWidget'
import NCROrderChartWidget from '@/modules/intelligence/dashboardWidgets/ncrOrderChartWidget'
import InvoiceDailyChartWidget from '@/modules/intelligence/dashboardWidgets/invoiceDailyChartWidget'
import InvoiceMonthlyChartWidget from '@/modules/intelligence/dashboardWidgets/invoiceMonthlyChartWidget'
import OrderDIFOTMonthlyOperationalWidget from '@/modules/intelligence/dashboardWidgets/orderDIFOTMonthlyOperationalWidget'
import OrderDIFOTMonthlyCollectionWidget from '@/modules/intelligence/dashboardWidgets/orderDIFOTMonthlyCollectionWidget'

import AnalyticsReportLink from '@/modules/intelligence/components/analyticsReportLink'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import { CreditTypes } from '@/store/types'
import { ChartDateRangeTypes } from '@/modules/intelligence/types'

export default {
  name: 'AnalyticsContactDashboard',
  components: {
    DashboardContentBox,
    DashboardTabbedContentBox,
    AnalyticsWidget,
    CustomerAgedBalanceChartWidget,
    AccountsReceivableSummaryWidget,
    FinancialBreakdownWidget,
    OrderValueChartWidget,
    OrderDailyChartWidget,
    OrderMonthlyChartWidget,
    NCROrderChartWidget,
    InvoiceDailyChartWidget,
    InvoiceMonthlyChartWidget,
    OrderDIFOTMonthlyOperationalWidget,
    OrderDIFOTMonthlyCollectionWidget,
    AnalyticsReportLink,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    creditType: String,
    contactId: Number,
    contactName: String
  },
  computed: {
    isCreditTypeAccount() {
      return this.creditType == CreditTypes.account
    },
    isDashboardAvailable() {
      if(this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceAccount) || this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceOrder))
        return true
      return false
    },
    orderReportLink() {
      let queryString = buildUrl({
        queryParams: {
          contactId: this.contactId,
          contactName: this.contactName
        },
        hash: this.fullReportTabId 
      })

      return `/analytics/reports/orders/ordervaluegroups${queryString}`
    }
  },
  created() {
    this.ChartDateRangeTypes = ChartDateRangeTypes
    this.NCROrderChartTypes = NCROrderChartWidget.NCROrderChartTypes
  },
  data() {
    return {
      defaultOrderValueFilter: {    
        startDate: moment().local().subtract(12, 'months').startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingOrderValueFilter: {},
      appliedOrderValueFilter: {},

      defaultNCRFilter: {    
        startDate: moment().local().subtract(12, 'months').startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingNCRFilter: {},
      appliedNCRFilter: {},

      // Tab ID of the full summary report page for orders
      // So we can link to the correct tab
      fullReportTabId: 'tab-ordervalue-glass'
    }
  },
  mounted() {
    this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
    this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

    this.editingNCRFilter = _.clone(this.defaultNCRFilter)
    this.appliedNCRFilter = _.clone(this.defaultNCRFilter)
  },
  methods: {
    applyOrderValueFilter() {
      this.appliedOrderValueFilter = _.clone(this.editingOrderValueFilter)
    },
    resetOrderValueFilter() {
      this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
      this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)
    },
    applyNCRFilter() {
      this.appliedNCRFilter = _.clone(this.editingNCRFilter)
    },
    resetNCRFilter() {
      this.editingNCRFilter = _.clone(this.defaultNCRFilter)
      this.appliedNCRFilter = _.clone(this.defaultNCRFilter)
    },
    setOrderFullReportTab(tabId) {
      this.fullReportTabId = tabId
    }
  }
}
</script>